/**
 * Zod schema for the Add Payment Account modal (CNP-133). Messages are i18n keys (resolved
 * via `t()` in FieldError); bounds match legacy mdvipconnect. The single RHF form holds
 * { card, bank, billing }; `makePaymentSchema` validates the active tab + billing and lets
 * zod strip the inactive tab's slice.
 */
import { z } from 'zod';
import {
  detectNetwork,
  expectedCardLength,
  expectedCvvLength,
  isNotPast,
  isNotTooFarFuture,
  isValidRoutingNumber,
} from './paymentAccount.format';
import type { PaymentTab } from './paymentAccount.types';

export const VALIDATION_KEYS = {
  cardNumberRequired: 'payments.validation.cardNumberRequired',
  cardNumberInvalid: 'payments.validation.cardNumberInvalid',
  expiryRequired: 'payments.validation.expiryRequired',
  expiryInvalid: 'payments.validation.expiryInvalid',
  expiryPast: 'payments.validation.expiryPast',
  expiryFuture: 'payments.validation.expiryFuture',
  cvvRequired: 'payments.validation.cvvRequired',
  cvvInvalid: 'payments.validation.cvvInvalid',
  routingRequired: 'payments.validation.routingRequired',
  routingInvalid: 'payments.validation.routingInvalid',
  accountNumberRequired: 'payments.validation.accountNumberRequired',
  accountNumberInvalid: 'payments.validation.accountNumberInvalid',
  bankNameRequired: 'payments.validation.bankNameRequired',
  billingNameRequired: 'payments.validation.billingNameRequired',
  billingLine1Required: 'payments.validation.billingLine1Required',
  billingLine1TooLong: 'payments.validation.billingLine1TooLong',
  billingLine2TooLong: 'payments.validation.billingLine2TooLong',
  billingCityRequired: 'payments.validation.billingCityRequired',
  billingCityTooLong: 'payments.validation.billingCityTooLong',
  billingStateRequired: 'payments.validation.billingStateRequired',
  billingZipRequired: 'payments.validation.billingZipRequired',
  billingZipInvalid: 'payments.validation.billingZipInvalid',
} as const;

/** Connect parity: exactly 5 digits, no ZIP+4. */
const ZIP_RE = /^\d{5}$/;
const ADDRESS_MAX = 255;
const CITY_MAX = 26;

function makeCardSchema(now: Date) {
  return z
    .object({
      cardNumber: z.string(),
      expiry: z.string(),
      cvv: z.string(),
      isHsa: z.boolean(),
      makeDefault: z.boolean(),
      autoPay: z.boolean(),
    })
    .superRefine((card, ctx) => {
      const panDigits = card.cardNumber.replace(/\D/g, '');
      if (panDigits.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['cardNumber'], message: VALIDATION_KEYS.cardNumberRequired });
      } else if (panDigits.length !== expectedCardLength(detectNetwork(card.cardNumber))) {
        // Amex 15 digits, all other networks 16 (per finance).
        ctx.addIssue({ code: 'custom', path: ['cardNumber'], message: VALIDATION_KEYS.cardNumberInvalid });
      }

      if (card.expiry.trim() === '') {
        ctx.addIssue({ code: 'custom', path: ['expiry'], message: VALIDATION_KEYS.expiryRequired });
      } else if (!/^\d{2}\/\d{2}$/.test(card.expiry)) {
        ctx.addIssue({ code: 'custom', path: ['expiry'], message: VALIDATION_KEYS.expiryInvalid });
      } else if (!isNotPast(card.expiry, now)) {
        ctx.addIssue({ code: 'custom', path: ['expiry'], message: VALIDATION_KEYS.expiryPast });
      } else if (!isNotTooFarFuture(card.expiry, now)) {
        ctx.addIssue({ code: 'custom', path: ['expiry'], message: VALIDATION_KEYS.expiryFuture });
      }

      const cvvDigits = card.cvv.replace(/\D/g, '');
      if (cvvDigits.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['cvv'], message: VALIDATION_KEYS.cvvRequired });
      } else if (cvvDigits.length !== expectedCvvLength(detectNetwork(card.cardNumber))) {
        ctx.addIssue({ code: 'custom', path: ['cvv'], message: VALIDATION_KEYS.cvvInvalid });
      }
    });
}

/**
 * Card-slice validation for the Edit Payment Account modal. Only `expiry` is validated —
 * card number/CVV aren't rendered on Edit (PAN is immutable), so unlike `makeCardSchema` they're
 * absent here. `isHsa`/`makeDefault` need no runtime checks (checkbox/radio values are always valid).
 */
function makeCardEditSchema(now: Date) {
  return z.object({ expiry: z.string(), isHsa: z.boolean(), makeDefault: z.boolean() }).superRefine((card, ctx) => {
    if (card.expiry.trim() === '') {
      ctx.addIssue({ code: 'custom', path: ['expiry'], message: VALIDATION_KEYS.expiryRequired });
    } else if (!/^\d{2}\/\d{2}$/.test(card.expiry)) {
      ctx.addIssue({ code: 'custom', path: ['expiry'], message: VALIDATION_KEYS.expiryInvalid });
    } else if (!isNotPast(card.expiry, now)) {
      ctx.addIssue({ code: 'custom', path: ['expiry'], message: VALIDATION_KEYS.expiryPast });
    } else if (!isNotTooFarFuture(card.expiry, now)) {
      ctx.addIssue({ code: 'custom', path: ['expiry'], message: VALIDATION_KEYS.expiryFuture });
    }
  });
}

function makeBankSchema() {
  return z
    .object({
      routingNumber: z.string(),
      accountNumber: z.string(),
      bankName: z.string(),
      accountType: z.enum(['checking', 'savings', 'other']),
      isHsa: z.boolean(),
      makeDefault: z.boolean(),
      autoPay: z.boolean(),
    })
    .superRefine((bank, ctx) => {
      const routingDigits = bank.routingNumber.replace(/\D/g, '');
      if (routingDigits.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['routingNumber'], message: VALIDATION_KEYS.routingRequired });
      } else if (!isValidRoutingNumber(routingDigits)) {
        ctx.addIssue({ code: 'custom', path: ['routingNumber'], message: VALIDATION_KEYS.routingInvalid });
      }

      const accountDigits = bank.accountNumber.replace(/\D/g, '');
      if (accountDigits.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['accountNumber'], message: VALIDATION_KEYS.accountNumberRequired });
      } else if (!/^\d{8,17}$/.test(accountDigits)) {
        ctx.addIssue({ code: 'custom', path: ['accountNumber'], message: VALIDATION_KEYS.accountNumberInvalid });
      }

      if (bank.bankName.trim() === '') {
        ctx.addIssue({ code: 'custom', path: ['bankName'], message: VALIDATION_KEYS.bankNameRequired });
      }
    });
}

/**
 * Bank-slice validation for the Edit Payment Account modal. Unlike `makeCardEditSchema`, there's no
 * date field to validate — bank accounts have no expiry, so `accountType`/`isHsa`/`makeDefault`
 * need no runtime checks at all (select/checkbox/radio values are always valid).
 */
function makeBankEditSchema() {
  return z.object({
    accountType: z.enum(['checking', 'savings', 'other']),
    isHsa: z.boolean(),
    makeDefault: z.boolean(),
  });
}

function makeBillingSchema(required: boolean) {
  const base = z.object({
    name: z.string(),
    line1: z.string(),
    line2: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
  });

  if (!required) return base;

  return base.superRefine((billing, ctx) => {
    if (billing.name.trim() === '') {
      ctx.addIssue({ code: 'custom', path: ['name'], message: VALIDATION_KEYS.billingNameRequired });
    }
    if (billing.line1.trim() === '') {
      ctx.addIssue({ code: 'custom', path: ['line1'], message: VALIDATION_KEYS.billingLine1Required });
    } else if (billing.line1.length > ADDRESS_MAX) {
      ctx.addIssue({ code: 'custom', path: ['line1'], message: VALIDATION_KEYS.billingLine1TooLong });
    }
    if (billing.line2.length > ADDRESS_MAX) {
      ctx.addIssue({ code: 'custom', path: ['line2'], message: VALIDATION_KEYS.billingLine2TooLong });
    }
    if (billing.city.trim() === '') {
      ctx.addIssue({ code: 'custom', path: ['city'], message: VALIDATION_KEYS.billingCityRequired });
    } else if (billing.city.length > CITY_MAX) {
      ctx.addIssue({ code: 'custom', path: ['city'], message: VALIDATION_KEYS.billingCityTooLong });
    }
    if (billing.state.trim() === '') {
      ctx.addIssue({ code: 'custom', path: ['state'], message: VALIDATION_KEYS.billingStateRequired });
    }
    if (billing.zip.trim() === '') {
      ctx.addIssue({ code: 'custom', path: ['zip'], message: VALIDATION_KEYS.billingZipRequired });
    } else if (!ZIP_RE.test(billing.zip.trim())) {
      ctx.addIssue({ code: 'custom', path: ['zip'], message: VALIDATION_KEYS.billingZipInvalid });
    }
  });
}

export interface MakePaymentSchemaOptions {
  tab: PaymentTab;
  /** Whether the billing sub-form must be filled in (no saved address, or the alternate form is open). */
  billingRequired: boolean;
  /** Injectable clock so expiry bounds are deterministic in tests. */
  now?: Date;
}

/** Build the validation schema for the active tab + billing. */
export function makePaymentSchema({ tab, billingRequired, now = new Date() }: MakePaymentSchemaOptions) {
  const billing = makeBillingSchema(billingRequired);
  if (tab === 'credit-card') {
    return z.object({ card: makeCardSchema(now), billing });
  }
  return z.object({ bank: makeBankSchema(), billing });
}

export interface MakeEditCardSchemaOptions {
  /** Whether the billing sub-form must be filled in (member opened "Change"). */
  billingRequired: boolean;
  /** Injectable clock so expiry bounds are deterministic in tests. */
  now?: Date;
}

/** Build the validation schema for the Edit Payment Account modal — credit card. */
export function makeEditCardSchema({ billingRequired, now = new Date() }: MakeEditCardSchemaOptions) {
  return z.object({ card: makeCardEditSchema(now), billing: makeBillingSchema(billingRequired) });
}

export interface MakeEditBankSchemaOptions {
  /** Whether the billing sub-form must be filled in (member opened "Change"). */
  billingRequired: boolean;
}

/** Build the validation schema for the Edit Payment Account modal — bank account. */
export function makeEditBankSchema({ billingRequired }: MakeEditBankSchemaOptions) {
  return z.object({ bank: makeBankEditSchema(), billing: makeBillingSchema(billingRequired) });
}
